/**
 * monitoring.ts — Error-Tracking, Web Vitals und Usage-Analytics via Supabase.
 *
 * Verantwortung:
 *   - Globale JS-Errors und Promise-Rejections abfangen
 *   - Web Vitals (LCP, FID, CLS) messen und persistieren
 *   - Manuelle `logError()`-Aufrufe aus dem App-Code entgegennehmen
 *   - Usage-Events (`trackEvent`) für Analytics-Dashboards
 *
 * Architektur:
 *   Der Supabase-Client wird via `initMonitoring(supabaseClient)` injected.
 *   Dadurch hat dieses Modul KEINE direkte Abhängigkeit zu `lib/supabase.js`
 *   und kann unabhängig migriert/getestet werden.
 *
 * Nutzung:
 *   import { initMonitoring, logError, logVital } from './lib/monitoring';
 *   initMonitoring(supabaseClient);            // einmalig in main.jsx
 *   logError(error, 'MeinKomponent');          // manuell
 *   trackEvent('login');                       // Usage-Analytics
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { MS_PER_DAY } from './format.js';

// ════════════════════════════════════════════════════════════════════
// Public types
// ════════════════════════════════════════════════════════════════════

/** Web-Vital-Metrik (analog zum web-vitals npm-Paket). */
export interface WebVital {
  name: string;
  value: number;
  delta: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

/** Roher Monitoring-Event aus der DB (für die Admin-Anzeige). */
export interface MonitoringEvent {
  id: string;
  typ: MonitoringEventType;
  message: string;
  stack?: string | null;
  context?: string | null;
  url?: string | null;
  user_id?: string | null;
  user_agent?: string | null;
  meta?: Record<string, unknown> | null;
  created_at: string;
}

/** Aggregierte Monitoring-Statistik (für das Admin-Dashboard). */
export interface MonitoringStats {
  /** Anzahl Errors in den letzten 24 Stunden. */
  last24h: number;
  /** Anzahl Errors in den letzten 7 Tagen. */
  last7d: number;
}

/**
 * Bekannte Usage-Event-Typen.
 * Wird in `trackEvent()` als Compile-Zeit-Schutz verwendet, sodass
 * Tippfehler bereits beim Build auffallen statt erst zur Laufzeit.
 */
export type UsageEventType =
  | 'login'
  | 'auftrag_erstellt'
  | 'angebot_abgegeben'
  | 'chat_nachricht';

// ════════════════════════════════════════════════════════════════════
// Internal types & state
// ════════════════════════════════════════════════════════════════════

type MonitoringEventType = 'error' | 'vital' | 'info';

/** Optionale Zusatzfelder beim Schreiben eines Monitoring-Events. */
interface WriteEventExtra {
  stack?: string | null;
  context?: string | null;
  meta?: Record<string, unknown>;
}

const KNOWN_EVENT_TYPES: readonly UsageEventType[] = [
  'login',
  'auftrag_erstellt',
  'angebot_abgegeben',
  'chat_nachricht',
];

let _sb: SupabaseClient | null = null;
let _userId: string | null = null;
let _initialized = false;

// Throttle: max 10 Events pro Minute (Schutz gegen Endlos-Schleifen)
let _eventCount = 0;
let _resetTimer: ReturnType<typeof setTimeout> | null = null;
const MAX_EVENTS_PER_MIN = 10;

function canLog(): boolean {
  if (_eventCount >= MAX_EVENTS_PER_MIN) return false;
  _eventCount++;
  if (!_resetTimer) {
    _resetTimer = setTimeout(function () {
      _eventCount = 0;
      _resetTimer = null;
    }, 60000);
  }
  return true;
}

/**
 * Schreibt einen Event in `monitoring_events`.
 * Fire-and-forget: Fehler beim Schreiben werden geschluckt, damit
 * Monitoring NIEMALS die App selbst zum Crash bringt.
 */
async function writeEvent(
  typ: MonitoringEventType,
  message: string,
  extra: WriteEventExtra = {},
): Promise<void> {
  if (!_sb || !canLog()) return;
  try {
    await _sb.from('monitoring_events').insert({
      typ: typ,
      message: (message || '').substring(0, 500),
      stack: (extra.stack || '').substring(0, 2000),
      context: (extra.context || '').substring(0, 100),
      url: window.location.pathname,
      user_id: _userId || null,
      user_agent: navigator.userAgent.substring(0, 200),
      meta: extra.meta || {},
    });
  } catch (e: unknown) {
    // Monitoring darf nie die App crashen
    const errMsg = e instanceof Error ? e.message : String(e);
    console.warn('[Monitoring] write failed:', errMsg);
  }
}

// ════════════════════════════════════════════════════════════════════
// Public API
// ════════════════════════════════════════════════════════════════════

/**
 * Loggt einen Fehler — sowohl auf der Browser-Konsole als auch in
 * `monitoring_events` (sofern initialisiert).
 *
 * Akzeptiert beliebige Fehler-Werte: `Error`-Objekte, Strings,
 * `null`/`undefined` oder geworfene Plain-Objekte.
 */
export function logError(error: unknown, context?: string): void {
  let msg = '';
  let stack: string | undefined;
  if (error instanceof Error) {
    msg = error.message;
    stack = error.stack;
  } else if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    msg = typeof m === 'string' ? m : String(m);
  } else if (error !== null && error !== undefined) {
    msg = String(error);
  }
  console.error('[DRPX]', context || '', msg);
  void writeEvent('error', msg, { stack: stack, context: context });
}

/**
 * Loggt einen Web-Vital-Eintrag.
 */
export function logVital(metric: WebVital): void {
  void writeEvent('vital', metric.name, {
    context: 'web-vital',
    meta: {
      name: metric.name,
      value: Math.round(metric.value),
      rating: metric.rating,
      delta: Math.round(metric.delta),
    },
  });
}

/**
 * Setzt die User-ID, die zukünftigen Events angehängt wird.
 * Aufrufen nach Login, mit `null` zurücksetzen beim Logout.
 */
export function setMonitoringUser(userId: string | null): void {
  _userId = userId;
}

/**
 * Lädt die letzten N Monitoring-Events. Nur für Admin.
 *
 * @param limit - Maximale Anzahl Events (Default 50)
 */
export async function getMonitoringEvents(limit?: number): Promise<MonitoringEvent[]> {
  if (!_sb) return [];
  try {
    const { data } = await _sb
      .from('monitoring_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit || 50);
    return (data as MonitoringEvent[] | null) || [];
  } catch (_e: unknown) {
    return [];
  }
}

/**
 * Lädt aggregierte Monitoring-Statistiken (Error-Count 24h und 7d).
 * Nur für Admin.
 */
export async function getMonitoringStats(): Promise<MonitoringStats> {
  if (!_sb) return { last24h: 0, last7d: 0 };
  try {
    const now = Date.now();
    const h24 = new Date(now - MS_PER_DAY).toISOString();
    const d7 = new Date(now - 7 * MS_PER_DAY).toISOString();

    const [r24, r7] = await Promise.all([
      _sb
        .from('monitoring_events')
        .select('id', { count: 'exact', head: true })
        .eq('typ', 'error')
        .gte('created_at', h24),
      _sb
        .from('monitoring_events')
        .select('id', { count: 'exact', head: true })
        .eq('typ', 'error')
        .gte('created_at', d7),
    ]);

    return {
      last24h: r24.count || 0,
      last7d: r7.count || 0,
    };
  } catch (_e: unknown) {
    return { last24h: 0, last7d: 0 };
  }
}

/**
 * Initialisiert das Monitoring-Modul. Einmalig in `main.jsx` aufrufen,
 * sobald der Supabase-Client verfügbar ist.
 *
 * Registriert globale Error-Listener und startet die Web-Vitals-Messung.
 * Mehrfaches Aufrufen ist unschädlich (idempotent).
 */
export function initMonitoring(supabaseClient: SupabaseClient): void {
  if (_initialized) return;
  _initialized = true;
  _sb = supabaseClient;

  // 1. Unhandled JS Errors
  window.addEventListener('error', function (event: ErrorEvent) {
    logError(event.error || event.message, 'window.onerror');
  });

  // 2. Unhandled Promise Rejections
  window.addEventListener('unhandledrejection', function (event: PromiseRejectionEvent) {
    const reason: unknown = event.reason;
    logError(reason, 'unhandledrejection');
  });

  // 3. Web Vitals (wenn verfügbar)
  try {
    if ('PerformanceObserver' in window) {
      // LCP — Largest Contentful Paint
      const lcpObs = new PerformanceObserver(function (list) {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) {
          logVital({
            name: 'LCP',
            value: last.startTime,
            delta: last.startTime,
            rating: last.startTime < 2500 ? 'good' : last.startTime < 4000 ? 'needs-improvement' : 'poor',
          });
        }
      });
      lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });

      // FID — First Input Delay
      const fidObs = new PerformanceObserver(function (list) {
        const entry = list.getEntries()[0] as PerformanceEventTiming | undefined;
        if (entry) {
          const fid = entry.processingStart - entry.startTime;
          logVital({
            name: 'FID',
            value: fid,
            delta: fid,
            rating: fid < 100 ? 'good' : fid < 300 ? 'needs-improvement' : 'poor',
          });
        }
      });
      fidObs.observe({ type: 'first-input', buffered: true });

      // CLS — Cumulative Layout Shift
      let clsValue = 0;
      const clsObs = new PerformanceObserver(function (list) {
        for (const entry of list.getEntries()) {
          const lsEntry = entry as PerformanceEntry & {
            hadRecentInput?: boolean;
            value?: number;
          };
          if (!lsEntry.hadRecentInput && typeof lsEntry.value === 'number') {
            clsValue += lsEntry.value;
          }
        }
      });
      clsObs.observe({ type: 'layout-shift', buffered: true });

      // CLS nach 5s loggen (stabil genug)
      setTimeout(function () {
        logVital({
          name: 'CLS',
          value: clsValue,
          delta: clsValue,
          rating: clsValue < 0.1 ? 'good' : clsValue < 0.25 ? 'needs-improvement' : 'poor',
        });
      }, 5000);
    }
  } catch (_e: unknown) {
    // Web Vitals sind nice-to-have, nicht kritisch
  }
}

// ════════════════════════════════════════════════════════════════════
// Usage-Analytics: trackEvent
// ════════════════════════════════════════════════════════════════════
// Schreibt ein User-Event in usage_events. Wird an strategischen
// Stellen aufgerufen (Login, Auftrag-Erstellung, Angebot, Chat).
// Fire-and-forget — Fehler werden geschluckt, niemals der App-Flow
// blockiert. Server-seitiges Rate-Limit (100/h) verhindert Spam.
// ════════════════════════════════════════════════════════════════════

/**
 * Trackt ein Usage-Event für den aktuellen User.
 * Compile-Zeit-Sicherheit über den `UsageEventType`-Union — Tippfehler
 * werden vom TypeScript-Compiler abgefangen, bevor der Code überhaupt
 * im Browser landet.
 */
export function trackEvent(eventType: UsageEventType): void {
  if (!_sb || !_userId) return;
  // Defensiver Runtime-Check für untypisierte JS-Caller (bis alle Caller
  // migriert sind). Für TS-Caller ist dieser Pfad unreachable, dank des
  // strikten UsageEventType.
  if (KNOWN_EVENT_TYPES.indexOf(eventType) === -1) {
    console.warn('[trackEvent] unknown event_type:', eventType);
    return;
  }
  // Fire-and-forget — kein await, keine Fehler-Propagation
  void _sb
    .from('usage_events')
    .insert({
      user_id: _userId,
      event_type: eventType,
    })
    .then(function (r: { error: { message: string } | null }) {
      if (r.error) console.warn('[trackEvent]', r.error.message);
    });
}
